import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, FormControl, TextField, FormHelperText } from "@mui/material/";
import { strings } from "../../../common/i18n";
import config from "../../../config";
import actions from "../../../Store/actions";
import { SelectAll } from "../../../common/components";
import SetPasswordDialog from "../../../common/components/SetPasswordDialog";

type Props = {
    disabled: boolean,
    onSubmit: Function,
    submitted: boolean
};

/**
 * Account Form Component
 */
export default ({ disabled = false, setDisabled, submitted = false }: Props) => {
    const { accounts, auth, typesList } = useSelector((state) => state);
    const dispatch = useDispatch();
    const [errors, setErrors] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const { item } = accounts;
    const [loading, setLoading] = useState(false);

    const handleChange = (name: string) => async (event: Event) => {
        const { value } = event.target;
        await dispatch(actions.accounts.create.itemUpdate({ [name]: value }));
    };

    const onDialogClose = () => setDialogOpen(false);
    const onDialogOpen = () => setDialogOpen(true);

    const onEmailBlur = useCallback(() => {
        let next = null;
        if (item.email.length && !item.email.match(config.regex.email)) {
            next = strings("errors.emailInvalid");
        } else if (
            accounts.data.findIndex((account) => {
                return account.id !== item.id && account.email === item.email;
            }) >= 0
        ) {
            next = strings("errors.emailTaken");
        }
        setErrors({ ...errors, email: next });
    }, [errors, item]);

    const onGroupsChange = useCallback(
        (ids) => {
            const next = ids.map((id) => {
                const index = typesList.groups.byId[id];
                return typesList.groups.data[index];
            });
            dispatch(actions.accounts.create.itemUpdate({ groups: next }));
            onValidate();
        },
        [typesList]
    );

    useEffect(() => {
        onValidate();
    }, [item]);

    const onValidate = useCallback(() => {
        let next = true;
        if (errors.email || !item.email) {
            next = false;
        } else if (
            !(
                item.displayName &&
                item.firstName &&
                item.surname &&
                item.groups.length > 0
            )
        ) {
            next = false;
        }
        setDisabled(next);
        return next;
    }, [errors, item]);

    const fieldDisabled = disabled;
    // Disable this for DS
    //|| item.isPrimary === false;

    return (
        <>
            <FormControl fullWidth variant="standard">
                <TextField
                    variant="standard"
                    required
                    size="small"
                    disabled={Boolean(item.id) || fieldDisabled}
                    error={submitted && (Boolean(errors.email) || !item.email)}
                    helperText={
                        submitted
                            ? (errors.email || (!item.email ? strings("errors.Emailrequired") : null))
                            : null
                    }

                    label={strings("email")}
                    onBlur={onEmailBlur}
                    onChange={handleChange("email")}
                    value={item.email}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" variant="standard">
                <TextField
                    variant="standard"
                    size="small"
                    disabled={fieldDisabled}
                    label={strings("firstName")}
                    onChange={handleChange("firstName")}
                    value={item.firstName}
                    error={submitted && !item.firstName}
                    helperText={submitted && !item.firstName ? strings("errors.FirstNameRequired") : null}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" variant="standard">
                <TextField
                    variant="standard"
                    size="small"
                    disabled={fieldDisabled}
                    label={strings("surname")}
                    onChange={handleChange("surname")}
                    value={item.surname}
                    error={submitted && !item.surname}
                    helperText={submitted && !item.surname ? strings("errors.SurNameRequired") : null}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" variant="standard">
                <TextField
                    variant="standard"
                    size="small"
                    disabled={fieldDisabled}
                    label={strings("displayName")}
                    onChange={handleChange("displayName")}
                    value={item.displayName}
                    error={submitted && !item.displayName}
                    helperText={submitted && !item.displayName ? strings("errors.DisplayNameRequired") : null}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" error={!item.groups || item.groups.length === 0}>
                <SelectAll
                    disabled={fieldDisabled}
                    items={typesList.groups.data}
                    onChange={onGroupsChange}
                    title={strings("permissions")}
                    value={item.groups.map(({ id }) => id)}
                />
                <FormHelperText>
                    {!item.groups || submitted && item.groups.length === 0 ? strings("errors.selectAtLeastOne") : null}
                </FormHelperText>
            </FormControl>
            {false && auth.identityId === item.identityId && (
                <FormControl fullWidth margin="normal">
                    <Button
                        disabled={disabled || loading}
                        onClick={setDialogOpen}
                        size="small"
                    >
                        {strings("changePassword")}
                    </Button>
                </FormControl>
            )}

            <SetPasswordDialog onClose={onDialogClose} open={dialogOpen} />
        </>
    );
};
